import { Box, Collapse, Divider, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronUpIcon } from '@/components/icons/ChevronUpIcon';
import { TableIcon } from '@/components/icons/TableIcon';
import { WaterfallFilledIcon } from '@/components/icons/WaterfallFilledIcon';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';

import { SmartReviewOnboardingSection } from '../../../SmartReviewOnboardingModal.types';
import { useDocumentUploadStepContext } from '../context/documentUploadStep.context';
import { DOCUMENT_UPLOAD_COPY } from '../documentUpload.constants';
import { useDocumentUploadExitActions } from '../hooks/useDocumentUploadExitActions';
import { useRecommendedDocumentStatus } from '../hooks/useRecommendedDocumentStatus';
import { RecommendedDocumentChip } from './RecommendedDocumentChip';

const COLLAPSE_TIMEOUT_MS = 100;

interface RecommendedDocumentsPanelProps {
  showActions: boolean;
}

/**
 * The recommended-document accordion and the actions shown below the dropzone.
 * Expanding the accordion keeps the actions mounted so its added height moves
 * the actions and value-prop cards down instead of replacing them.
 */
export function RecommendedDocumentsPanel({
  showActions,
}: RecommendedDocumentsPanelProps) {
  const { householdId, isRecommendationsExpanded, toggleRecommendations } =
    useDocumentUploadStepContext();
  const onboardingTrowser = useTrowser(TrowserKey.SmartReviewOnboarding);
  const trackUserEvent = useTrackUserEvent();
  const {
    createBalanceSheetFromIntegration,
    canCreateBalanceSheet,
    isExiting,
  } = useDocumentUploadExitActions();
  const recommendedDocuments = useRecommendedDocumentStatus();

  if (!householdId) return null;

  const handleNoDocumentsClick = () => {
    onboardingTrowser.open({
      target: householdId,
      section: SmartReviewOnboardingSection.NoDocumentsPlan,
    });
    trackUserEvent('client_onboarding no_documents_waterfall_clicked', {
      householdId,
    });
  };

  return (
    <Stack spacing={4.5} mt={-1}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          bgcolor: COLORS.GRAY[50],
          px: 1,
          pt: 1,
          pb: isRecommendationsExpanded ? 1.5 : 1,
        }}
      >
        <Button
          variant="transparent"
          size="xs"
          aria-expanded={isRecommendationsExpanded}
          aria-controls="recommended-documents-content"
          endIcon={isRecommendationsExpanded ? ChevronUpIcon : ChevronDownIcon}
          iconProps={{ size: 16, 'aria-hidden': true }}
          onClick={toggleRecommendations}
          contentBoxSx={{ width: '100%' }}
          sx={{
            width: '100%',
            p: 0,
            border: 0,
            color: COLORS.TEXT.PRIMARY,
            justifyContent: 'stretch',
            textAlign: 'left',
            '&:hover': {
              border: 0,
              bgcolor: 'transparent',
              color: COLORS.TEXT.PRIMARY,
            },
          }}
        >
          {DOCUMENT_UPLOAD_COPY.recommendationsToggle}
        </Button>
        <Collapse
          id="recommended-documents-content"
          in={isRecommendationsExpanded}
          timeout={COLLAPSE_TIMEOUT_MS}
          easing="ease-in-out"
          unmountOnExit
        >
          <Stack spacing={1.5} pt={1}>
            <Divider sx={{ borderColor: COLORS.GRAY[300] }} />
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent="center"
            >
              {recommendedDocuments.map(({ label, isSatisfied }) => (
                <RecommendedDocumentChip
                  key={label}
                  label={label}
                  isSatisfied={isSatisfied}
                />
              ))}
            </Stack>
            <Divider
              sx={{
                width: 'calc(100% - 65px)',
                alignSelf: 'center',
                borderColor: COLORS.GRAY[200],
              }}
            />
            <Typography
              variant="body-xs"
              textAlign="center"
              sx={{ color: COLORS.TEXT.SECONDARY }}
            >
              {DOCUMENT_UPLOAD_COPY.recommendationsHelper}
            </Typography>
          </Stack>
        </Collapse>
      </Box>
      {showActions && (
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button
            variant="secondary"
            size="md"
            startIcon={WaterfallFilledIcon}
            iconProps={{ size: 16, 'aria-hidden': true }}
            onClick={handleNoDocumentsClick}
            disabled={isExiting}
          >
            {DOCUMENT_UPLOAD_COPY.noDocuments}
          </Button>
          {canCreateBalanceSheet && (
            <Button
              variant="secondary"
              size="md"
              startIcon={TableIcon}
              iconProps={{ size: 16, 'aria-hidden': true }}
              onClick={createBalanceSheetFromIntegration}
              loading={isExiting}
            >
              {DOCUMENT_UPLOAD_COPY.createBalanceSheet}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}
